/* @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import { Table, Tooltip } from 'antd'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  useUsersMeUserRoles,
  UserRole,
} from '@sportninja/common/hooks/useUsersMeUserRoles'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import { StarFilled, StarOutlined } from '@ant-design/icons'

/**
 * Props interface for the PlayerProfileMembership component
 * @interface PlayerProfileMembershipProps
 */
interface PlayerProfileMembershipProps {
  /**
   * Page number for pagination
   */
  page?: number
  /**
   * Number of items per page
   */
  perPage?: number
}

/**
 * Component that displays a player's memberships (user roles) in a table
 * @param {PlayerProfileMembershipProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const PlayerProfileMembership: React.FC<PlayerProfileMembershipProps> = ({
  page = 1,
  perPage = 50,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768

  // Use the hook to fetch user roles
  const { data, loading, error, refetch } = useUsersMeUserRoles(page, perPage)

  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1)

  // Columns configuration for the table
  const columns = [
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      width: 300,
      filterSearch: true,
      filters: data
        .map((role) => role.name)
        .filter((name): name is string => !!name)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((name) => ({ text: name, value: name })),
      onFilter: (value: string, record: UserRole) =>
        record.name?.includes(value) || false,
      sorter: (a: UserRole, b: UserRole) => {
        const orgA = a.name || ''
        const orgB = b.name || ''
        return orgA.localeCompare(orgB)
      },
      render: (_, record: UserRole) => {
        const routeRoot =
          record.type === 'team'
            ? ROUTES.TEAM_ROOT
            : record.type === 'schedule'
            ? ROUTES.SCHEDULE_ROOT
            : ROUTES.ORG_ROOT
        return (
          <Link
            to={`${routeRoot}/${record.id}`}
            css={css`
              text-decoration: none;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                max-width: 280px;
              `}
            >
              <img
                src={
                  getImageThumbnailId(record) ||
                  (record.type === 'team'
                    ? '/images/generic_placeholders/placeholder_team.png'
                    : record.type === 'competition'
                    ? '/images/generic_placeholders/placeholder_competition.png'
                    : record.type === 'player'
                    ? '/images/generic_placeholders/placeholder_player.png'
                    : '/images/generic_placeholders/placeholder_organization.png')
                }
                alt={record.name || 'Organization'}
                css={css`
                  width: 32px;
                  height: 32px;
                  border-radius: 8px;
                  object-fit: contain;
                  border: 1px solid ${colors.NEUTRAL_0};
                  flex-shrink: 0;
                `}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const target = e.target as HTMLImageElement
                  target.src =
                    record.type === 'team'
                      ? '/images/generic_placeholders/placeholder_team.png'
                      : record.type === 'competition'
                      ? '/images/generic_placeholders/placeholder_competition.png'
                      : record.type === 'player'
                      ? '/images/generic_placeholders/placeholder_player.png'
                      : '/images/generic_placeholders/placeholder_organization.png'
                }}
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  overflow: hidden;
                  width: 100%;
                `}
              >
                <Tooltip title={record.name || '-'}>
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.NEUTRAL_0}
                    style={`
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
                  >
                    {record.name || '-'}
                  </Text>
                </Tooltip>
                <Tooltip title={record.name_full || '-'}>
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style={`
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
                  >
                    {record.name_full || '-'}
                  </Text>
                </Tooltip>
              </div>
            </div>
          </Link>
        )
      },
    },
    {
      title: 'Entity Type',
      key: 'type',
      dataIndex: 'type',
      filterSearch: true,
      filters: data
        .map((role) => role.type)
        .filter((type): type is string => !!type)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((type) => ({ text: type, value: type })),
      onFilter: (value: string, record: UserRole) =>
        record.type?.includes(value) || false,
      sorter: (a: UserRole, b: UserRole) => {
        const typeA = a.type || ''
        const typeB = b.type || ''
        return typeA.localeCompare(typeB)
      },
      align: 'left',
      render: (_, record: UserRole) => {
        // Replace 'schedule' with 'competition'
        const displayType =
          record.type === 'schedule' ? 'competition' : record.type

        return (
          <div
            css={css`
              display: flex;
              justify-content: flex-start;
              align-items: center;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_0}
              style='text-transform: capitalize;'
            >
              {displayType || '-'}
            </Text>
          </div>
        )
      },
    },
    {
      title: 'User Roles',
      key: 'roles',
      dataIndex: 'roles',
      filterSearch: true,
      filters: [
        ...new Set(
          data
            .flatMap((role) => role.roles.map((r) => r.role_name))
            .filter((name): name is string => !!name)
        ),
      ].map((name) => ({ text: name, value: name })),
      onFilter: (value: string, record: UserRole) =>
        record.roles.some((role) => role.role_name === value),
      sorter: (a: UserRole, b: UserRole) => {
        const rolesA = a.roles.map((r) => r.role_name).join(', ')
        const rolesB = b.roles.map((r) => r.role_name).join(', ')
        return rolesA.localeCompare(rolesB)
      },
      align: 'left',
      render: (_, record: UserRole) => {
        // Remove duplicate role names
        const uniqueRoleNames = [
          ...new Set(record.roles.map((r) => r.role_name)),
        ].join(', ')
        const isTruncated = uniqueRoleNames.length > 30
        const displayText = isTruncated
          ? `${uniqueRoleNames.substring(0, 30)}...`
          : uniqueRoleNames || '-'

        return (
          <div
            css={css`
              display: flex;
              justify-content: flex-start;
              align-items: center;
            `}
          >
            <Tooltip
              title={uniqueRoleNames}
              placement='top'
              zIndex={10001}
              trigger='hover'
            >
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.NEUTRAL_0}
                style='text-transform: capitalize;'
              >
                {displayText}
              </Text>
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: 'Favourite',
      key: 'favorite',
      dataIndex: 'is_favorite',
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value: boolean, record: UserRole) =>
        record.is_favorite === value,
      sorter: (a: UserRole, b: UserRole) => {
        if (a.is_favorite === b.is_favorite) {
          return 0
        }
        return a.is_favorite ? -1 : 1 // Favorite comes first
      },
      align: 'center',
      render: (_, record: UserRole) => (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          {record.is_favorite ? (
            <StarFilled
              css={css`
                color: ${colors.PRIMARY};
                font-size: 18px;
              `}
            />
          ) : (
            <StarOutlined
              css={css`
                color: ${colors.NEUTRAL_50};
                font-size: 18px;
              `}
            />
          )}
        </div>
      ),
    },
  ]

  // Effect to fetch data when component mounts
  useEffect(() => {
    refetch()
  }, [refetch])

  // Effect to reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [page, perPage])

  return (
    <div
      className='player-profile-membership'
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 98px;
        margin-top: 24px;
        padding: 20px 16px;
        background-color: ${colors.SECONDARY_300};
        border: 1px solid #3f4753;
        border-radius: 8px;
        ${isMobile &&
        css`
          padding: 16px 8px;
        `}
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          padding-bottom: 16px;
          margin-bottom: 16px;
          border-bottom: 1px solid ${colors.SECONDARY_200};

          ${isMobile &&
          css`
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          `}
        >
          <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
            MEMBERSHIPS
          </Text>
          {/* <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.ATTENDANCE_GRAY}
          >
            Your Memberships
          </Text> */}
        </div>
      </div>

      {/* Table component */}
      <div
        css={css`
          overflow-x: auto;
          width: 100%;
        `}
      >
        <Table
          dataSource={data
            .map((userRole, index) => ({
              ...userRole,
              key: userRole.id || index,
            }))
            .sort((a, b) => a?.name?.localeCompare(b?.name))}
          // @ts-ignore
          columns={columns}
          loading={loading}
          pagination={{
            pageSize: 20,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          bordered={true}
          size='small'
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.ATTENDANCE_GRAY}
              >
                {error
                  ? 'Error Loading Memberships'
                  : 'You Have No Memberships'}
              </Text>
            ),
          }}
          css={css`
            .ant-table {
              border-radius: 8px;
              overflow: hidden;
            }
            .ant-table-container {
              border-radius: 8px;
              overflow: hidden;
            }
            .ant-table-thead > tr > th {
              background-color: ${colors.SECONDARY_500};
              color: ${colors.NEUTRAL_0};
              text-align: center;
            }
            .ant-table-thead > tr > th:first-of-type {
              border-top-left-radius: 8px;
              text-align: left;
              position: sticky;
              left: 0;
              z-index: 1;
            }
            .ant-table-thead > tr > th:last-child {
              border-top-right-radius: 8px;
            }
            .ant-table-tbody > tr > td {
              vertical-align: middle;
            }
            .ant-table-tbody > tr:nth-of-type(odd) > td {
              background-color: ${colors.SECONDARY_500};
            }
            .ant-table-tbody > tr:nth-of-type(even) > td {
              background-color: ${colors.SECONDARY_400};
            }
            .ant-table-tbody > tr > td:first-of-type {
              position: sticky;
              left: 0;
              z-index: 1;
            }
            .ant-table-tbody > tr:nth-of-type(odd) > td:first-of-type {
              background-color: ${colors.SECONDARY_500};
            }
            .ant-table-tbody > tr:nth-of-type(even) > td:first-of-type {
              background-color: ${colors.SECONDARY_400};
            }
            .ant-table-tbody > tr:last-child > td:first-of-type {
              border-bottom-left-radius: 8px;
            }
            .ant-table-tbody > tr:last-child > td:last-of-type {
              border-bottom-right-radius: 8px;
            }
            .ant-table-tbody > tr:hover > td {
              background-color: ${colors.SECONDARY_200} !important;
            }
            .ant-pagination {
              margin-top: 16px;
              text-align: center;
            }
            .ant-pagination-item,
            .ant-pagination-prev,
            .ant-pagination-next {
              background-color: ${colors.SECONDARY_400};
              border-color: ${colors.SECONDARY_200};
            }
            .ant-pagination-item-active {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
            }
            .ant-pagination-item a {
              color: ${colors.NEUTRAL_0};
            }
            .ant-pagination-item-active a {
              color: ${colors.NEUTRAL_100};
            }
            .ant-pagination-prev button,
            .ant-pagination-next button {
              color: ${colors.NEUTRAL_0};
            }
          `}
        />
      </div>
    </div>
  )
}

export default PlayerProfileMembership
